import { useEffect, useState } from 'react';

/**
 * Say when this platform is answering real calls with pretend providers.
 *
 * Written after every call on a live box ended as dead air. Real telephony,
 * mocked speech recognition: the calls connected, the clips played, the
 * customer spoke, and the mock recogniser returned nothing — so every call was
 * filed as nobody having said a word. The carrier bill was real and the leads
 * were spent.
 *
 * Nothing in the portal said so. The only evidence was one `info` line in the
 * syslog at start-up:
 *
 *     mock_modes_from_env: {"telephony": false, "stt": true, ...}
 *
 * THE COMBINATION IS THE FAULT, NOT EITHER HALF. Everything mocked is a demo,
 * and that is what `.env` ships as. Nothing mocked is production. Real
 * telephony with a mocked provider is the one arrangement that cannot be told
 * from a broken install by looking at it, which is why it needs a banner and
 * the other two do not.
 *
 * Deliberately NOT dismissible. The stale-bundle notice beside this one is,
 * because an operator may reasonably choose to carry on with an old page. Here
 * every minute it stays true costs money and burns leads, and dismissing it
 * would restore precisely the silence that made this expensive to find.
 */
export function MockModeNotice() {
  const [mocked, setMocked] = useState<string[]>([]);
  const [dialerMocked, setDialerMocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const read = () => {
      fetch('/health', { headers: { accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((body) => {
          if (cancelled) return;
          const names = Array.isArray(body?.pretending) ? body.pretending : [];
          setMocked(names.map(String));
          setDialerMocked(Boolean(body?.dialer_mocked));
        })
        .catch(() => {
          // A health check that cannot be reached says nothing either way.
        });
    };
    read();
    // Re-read, because this is fixed on the server while somebody watches
    // this page, and a banner that outlives the problem teaches people to
    // ignore banners.
    const timer = window.setInterval(read, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  if (!mocked.length && !dialerMocked) return null;

  const names: Record<string, string> = {
    stt: 'speech recognition',
    tts: 'speech synthesis',
    ai: 'the language model',
  };
  const listed = mocked.map((name) => names[name] ?? name).join(', ');

  return (
    <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
      {/* Two faults, said separately. A mocked recogniser ends every call in
          silence, which is loud. A mocked dialer makes every call look
          perfect and sends every disposition nowhere, which is silent — and
          would hide behind the other one if they shared a sentence. */}
      {dialerMocked && (
        <p className="mb-2 font-medium">
          The dialer is in mock mode, so no disposition is reaching Vicidial —
          transfers, dead air and callbacks are all being discarded. The calls
          look right and the leads are never updated. Turn{' '}
          <code className="font-mono">VICIDIAL_MOCK_MODE</code> off.
        </p>
      )}
      {mocked.length > 0 && (
      <>
      <p className="font-medium">
        This platform is taking real calls with {listed} in mock mode.
      </p>
      <p className="mt-1 text-xs leading-relaxed">
        Every call will connect, play its audio, and end having understood
        nothing — recorded as dead air, with the carrier billed for all of it.
        Turn the mock modes off on the <strong>Setup</strong> page, or in{' '}
        <code className="font-mono">/opt/glee/.env</code> (
        <code className="font-mono">STT_MOCK_MODE</code>,{' '}
        <code className="font-mono">TTS_MOCK_MODE</code>,{' '}
        <code className="font-mono">AI_MOCK_MODE</code>), then{' '}
        <code className="font-mono">sudo glee-restart</code>.
      </p>
      </>
      )}
    </div>
  );
}
